import { CreditCardPayment } from "@apac/components/checkout";
import type { Product } from "@b2bportal/purchase-api";
import {
  CardPaymentSelectors,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { TokenizeCardErrors } from "@hopper-b2b/types";
import { MobileFloatingSection } from "@hopper-b2b/ui";
import { useDeviceTypes } from "@hopper-b2b/utilities";
import { Grid } from "@material-ui/core";
import { Event, type TEvent } from "../../../../modules/events";

export type PaymentSelectorContentProps = {
  disabled?: boolean;
  showEditAction?: boolean;
  hideContinue?: boolean;
  isContinueDisabled?: boolean;
  onEdit?: () => void;
  onContinue?: () => void;
  setSpreedlyError?: (hasError: boolean) => void;
  setTokenizeErrors?: (errors: TokenizeCardErrors[]) => void;
  product?: Product;
  yearDigit?: "two" | "four";
};

export const PaymentSelectorContent = ({
  disabled,
  showEditAction,
  hideContinue,
  isContinueDisabled = true,
  onEdit,
  onContinue,
  setSpreedlyError,
  setTokenizeErrors,
  product,
  yearDigit,
}: PaymentSelectorContentProps) => {
  const { matchesMobile } = useDeviceTypes();
  const { t } = useI18nContext();

  const selectedPaymentMethodId = useSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId
  );

  const creditCards = useSelector(CardPaymentSelectors.getPaymentMethods);

  const isPaymentLoading = useSelector(
    CardPaymentSelectors.getIsPaymentLoading
  );

  const send = useCheckoutSend<TEvent>();

  const handleClickAddPaymentMethod = () => send(Event.OPEN_PAYMENT_FORM);

  const handleCloseAddPaymentMethod = () => send(Event.CLOSE_PAYMENT_FORM);

  const verifyPaymentMethod = (token: string) =>
    send({ type: Event.VERIFY_PAYMENT_METHOD, token });

  const deletePaymentMethod = (paymentId: string) =>
    send({ type: Event.DELETE_PAYMENT_METHOD, paymentMethod: { paymentId } });

  const setSelectedPaymentMethodId = (paymentId: string | undefined) =>
    send({
      type: Event.SET_SELECTED_PAYMENT_METHOD_ID,
      paymentMethod: { paymentId },
    });

  const openAddPaymentMethodModal = useSelector(
    CardPaymentSelectors.getOpenAddPaymentMethodModal
  );

  // To pass the type check
  const voidfunc = () => {
    return;
  };
  return (
    <>
      <CreditCardPayment
        title={t("paymentMethodTitle")}
        creditCards={creditCards}
        disabled={disabled}
        deletePaymentMethod={deletePaymentMethod}
        handleClickAddPaymentMethod={handleClickAddPaymentMethod}
        handleCloseAddPaymentMethod={handleCloseAddPaymentMethod}
        hasError={false}
        loading={isPaymentLoading}
        openAddPaymentMethodModal={openAddPaymentMethodModal}
        setOpenErrorPaymentModal={setSpreedlyError ?? voidfunc}
        setSelectedPaymentMethodId={setSelectedPaymentMethodId}
        setTokenizeErrors={setTokenizeErrors ?? voidfunc}
        verifyPaymentMethod={verifyPaymentMethod}
        selectedPaymentMethodId={selectedPaymentMethodId}
        showEditAction={showEditAction}
        onEdit={onEdit}
        product={product}
        yearDigit={yearDigit}
      />
      <Grid container spacing={2}>
        {matchesMobile && !isContinueDisabled && !hideContinue && (
          <Grid item>
            <MobileFloatingSection
              primaryButton={{
                children: t("continue"),
                onClick: onContinue ?? voidfunc,
                disabled: disabled || isContinueDisabled,
              }}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
